import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Marketplace, ProductGroup } from "@front/m19-api-client";
import { ProductGroupEx } from "@front/m19-models";
import { ProductGroupService } from "@front/m19-services";
import { IButtonDirective, MODAL_DATA, ModalRef } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  BulkUploadReportContentComponent,
  ReportCreation,
  ReportUpdate,
} from "@m19-board/shared/bulk-upload-modal/bulk-upload-report-content.component";
import { catchError, forkJoin, of } from "rxjs";
import { BulkImportService, ProductGroupBulkOperations } from "../bulk-import.service";
export interface ProductGroupBulkUploadReportModalData {
  productGroupBulkOperations: ProductGroupBulkOperations;
  accountId: string;
  marketplace: Marketplace;
}

@Component({
  imports: [TranslocoDirective, IButtonDirective, BulkUploadReportContentComponent],
  templateUrl: "./product-group-report-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupReportModalComponent {
  private readonly bulkImportService = inject(BulkImportService);
  private readonly productGroupService = inject(ProductGroupService);

  protected readonly modalData = inject(MODAL_DATA) as ProductGroupBulkUploadReportModalData;
  modalRef = inject(ModalRef);
  destroyRef = inject(DestroyRef);

  readonly fields = this.bulkImportService.ProductGroupBulkConfig;
  readonly creations = this.modalData.productGroupBulkOperations.creations;
  readonly updates = this.modalData.productGroupBulkOperations.updates;
  readonly errors = this.modalData.productGroupBulkOperations.errors;

  readonly reportCreations: ReportCreation<ProductGroup>[] = this.creations.map((creation) => ({
    createdEntity: creation.productGroup,
  }));
  readonly reportUpdates: ReportUpdate<ProductGroup>[] =
    this.updates?.map((update) => ({
      original: update.productGroupToUpdate,
      updated: {
        ...update.productGroupToUpdate,
        ...update.updatedFields,
        items: update.updatedFields.asins,
      },
    })) ?? [];

  readonly uploadButtonDisabled = this.creations.length === 0 && this.updates?.length === 0;

  uploadLoading = signal(false);

  uploadData(): void {
    this.uploadLoading.set(true);
    const errors: string[] = [];
    const creations =
      this.creations.length === 0
        ? of([] as ProductGroup[])
        : forkJoin(
            this.creations.map((creation) => {
              return this.productGroupService
                .createProductGroup(
                  this.modalData.accountId,
                  this.modalData.marketplace,
                  creation.productGroup.productGroupName,
                  creation.productGroup.items,
                )
                .pipe(
                  catchError((err: string[] | string) => {
                    if (typeof err == "string") {
                      errors.push(
                        `Impossible to create product group ${creation.productGroup.productGroupName}: ${err}`,
                      );
                    } else {
                      err.forEach((e) =>
                        errors.push(
                          `Impossible to create product group ${creation.productGroup.productGroupName}: ${e}`,
                        ),
                      );
                    }
                    return of(null);
                  }),
                );
            }),
          );

    const updates =
      this.updates?.length === 0
        ? of([] as ProductGroup[])
        : forkJoin(
            this.updates.map((update) => {
              return this.productGroupService
                .updateProductGroup(this.modalData.accountId, this.modalData.marketplace, {
                  accountId: this.modalData.accountId,
                  productGroupId: update.productGroupToUpdate.productGroupId,
                  productGroupName:
                    update.updatedFields.productGroupName ?? update.productGroupToUpdate.productGroupName,
                  items: update.updatedFields.asins,
                })
                .pipe(
                  catchError((err: string) => {
                    errors.push(
                      `Impossible to update product group ${update.productGroupToUpdate.productGroupName}: ${err}`,
                    );
                    return of(null);
                  }),
                );
            }),
          );
    forkJoin([creations, updates])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([created, updated]) => {
        this.uploadLoading.set(false);
        this.modalRef.close({
          created: created.filter((s): s is ProductGroup | ProductGroupEx => !!s),
          updated: updated.filter((s): s is ProductGroup | ProductGroupEx => !!s),
          errors: errors,
        });
        this.modalRef.close();
      });
  }
}
